// /grid - every site Grid keeps a row for, one row each, with its routes on the table
// side by side. Reached by URL only: it is not in the nav.
// docs/Y2026-Q4/design-grid-table.md
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  type OnTrack,
  type PortfolioEntry,
  useAgentPortfolio,
} from "../api/hooks/useAgentPortfolio";
import {
  type AttributeDef,
  type DashboardRow,
  useDashboard,
} from "../api/hooks/useDashboard";
import { useProjectsList } from "../api/hooks/useProjectsList";
import { achievableKwpOptions } from "../utils/achievableKwp";
import { formatOptionValue } from "../utils/attributeFormat";
import {
  LifecycleChip,
  OptionsOnTheTable,
  OwnerChip,
  RoutePointText,
  StageChip,
  VerdictChip,
} from "../components/PortfolioRowParts";
import {
  matchRouteApplications,
  type OptionRoute,
  parseOptionRoutes,
  type RouteApplication,
  routeOrder,
} from "../utils/optionRoutes";
import styles from "./GridTableRoute.module.css";
import { projectSpecialistPath } from "./learningAgentPaths";

const AGENT = "grid";

// The filter value for a row with no verdict written.
const NO_VERDICT = "none";

const VERDICTS: { value: string; label: string }[] = [
  { value: "on_track", label: "On track" },
  { value: "at_risk", label: "At risk" },
  { value: "blocked", label: "Blocked" },
  { value: NO_VERDICT, label: "Not set" },
];

interface Parties {
  tenant: string;
  landlord: string;
}

interface TableRow {
  entry: PortfolioEntry;
  parties: Parties;
  // Most advanced first (routeOrder).
  options: { route: OptionRoute; match: RouteApplication }[];
}

const verdictOf = (onTrack: OnTrack | undefined): string =>
  onTrack?.verdict || NO_VERDICT;

const distinct = (values: string[]): string[] =>
  [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );

// Committed and achievable installed capacity as the dashboard reads them: committed
// falls back to the platform's proposed figure, achievable goes through
// achievableKwpOptions so an inferred kWp reads the same on both pages.
const InstalledCapacity = ({
  row,
  definitions,
}: {
  row: DashboardRow;
  definitions: Map<string, AttributeDef>;
}) => {
  const committedDef = definitions.get("committed_kwp");
  const achievableDef = definitions.get("achievable_kwp");
  if (!committedDef || !achievableDef) return null;
  const committed = row.attributes["committed_kwp"]?.options ?? [];
  const proposed = row.pitched["committed_kwp"];
  const achievable = achievableKwpOptions(row) ?? [];
  return (
    <p className={styles.capacity}>
      Committed:{" "}
      {committed.length > 0
        ? committed.map((o) => formatOptionValue(committedDef, o.value)).join(" or ")
        : proposed
          ? `${formatOptionValue(committedDef, proposed.value)} (proposed)`
          : "not set"}
      <br />
      Achievable:{" "}
      {achievable.length > 0
        ? achievable.map((o, i) => (
            <span key={i}>
              {i > 0 && " or "}
              {formatOptionValue(achievableDef, o.value)}
              {o.inferred && " (inferred from grid kWac)"}
            </span>
          ))
        : "not set"}
    </p>
  );
};

// A route's details up to and including Capacity (the first detail where it names none)
// show at once; the rest, and the application, behind "See more".
const CAPACITY_LABEL = /^capacity$/i;

const RouteCell = ({
  route,
  match,
}: {
  route: OptionRoute;
  match: RouteApplication;
}) => {
  const [expanded, setExpanded] = useState(false);
  const capacityAt = route.points.findIndex((p) => CAPACITY_LABEL.test(p.label));
  const lead = capacityAt >= 0 ? capacityAt + 1 : Math.min(1, route.points.length);
  const points = expanded ? route.points : route.points.slice(0, lead);
  return (
    <div
      className={[styles.route, route.ruledOut && styles.ruledOut]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.routeHead}>
        {route.ruledOut && <span className={styles.ruledOutChip}>Ruled out</span>}
        {route.stage ? (
          <StageChip stage={route.stage} />
        ) : (
          !route.ruledOut && <span className={styles.muted}>Not offered</span>
        )}
      </div>
      {route.title && <p className={styles.routeTitle}>{route.title}</p>}
      {points.length > 0 && (
        <ul className={styles.points}>
          {points.map((point, i) => (
            <li key={i}>
              <RoutePointText point={point} />
            </li>
          ))}
        </ul>
      )}
      {expanded && (
        <p className={styles.reference}>
          {match.kind === "matched" ? (
            <>
              Application{" "}
              {match.application.references.join(" / ") || "with no reference"}
            </>
          ) : match.kind === "ambiguous" ? (
            <span className={styles.muted}>
              Application unclear: {match.candidates} at this stage
            </span>
          ) : (
            <span className={styles.muted}>No matching application</span>
          )}
        </p>
      )}
      <button
        type="button"
        className={styles.seeMore}
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? "See less" : "See more"}
      </button>
    </div>
  );
};

export const GridTableRoute = () => {
  const portfolio = useAgentPortfolio(AGENT);
  const projects = useProjectsList();
  const dashboard = useDashboard();
  const [verdict, setVerdict] = useState("");
  const [stage, setStage] = useState("");
  const [tenant, setTenant] = useState("");
  const [landlord, setLandlord] = useState("");

  const rows = useMemo<TableRow[]>(() => {
    if (!portfolio.data) return [];
    const parties = new Map<string, Parties>(
      (projects.data ?? []).map((p) => [
        p.project_id,
        { tenant: p.tenant?.trim() ?? "", landlord: p.landlord?.trim() ?? "" },
      ]),
    );
    return [...portfolio.data.enrolled, ...portfolio.data.unenrolled]
      .map((entry) => {
        // Matched in the agent's own order, then shown most advanced first.
        const parsed = parseOptionRoutes(entry.row?.options ?? "");
        const matches = matchRouteApplications(parsed, entry.row?.applications ?? []);
        return {
          entry,
          parties: parties.get(entry.project_id) ?? { tenant: "", landlord: "" },
          options: parsed
            .map((route, i) => ({ route, match: matches[i] }))
            .sort((a, b) => routeOrder(a.route, b.route)),
        };
      })
      .sort((a, b) => a.entry.project_name.localeCompare(b.entry.project_name));
  }, [portfolio.data, projects.data]);

  const sizes = useMemo(
    () => ({
      byProject: new Map(
        (dashboard.data?.projects ?? []).map((r) => [r.project_id, r]),
      ),
      definitions: new Map(
        (dashboard.data?.attributes ?? []).map((a) => [a.key, a]),
      ),
    }),
    [dashboard.data],
  );

  if (portfolio.isLoading) return <p className={styles.muted}>Loading Grid's sites…</p>;
  if (portfolio.error || !portfolio.data) {
    return <p className={styles.error}>Could not load Grid's sites.</p>;
  }

  const shown = rows.filter(
    (r) =>
      (!verdict || verdictOf(r.entry.row?.on_track) === verdict) &&
      (!stage || r.entry.row?.lifecycle === stage) &&
      (!tenant || r.parties.tenant === tenant) &&
      (!landlord || r.parties.landlord === landlord),
  );
  // Columns are positions, the most advanced route on the left. Options written in no
  // numbered shape still show, as prose in the first column.
  const unparsed = (r: TableRow): boolean =>
    r.options.length === 0 && !!r.entry.row?.options.trim();
  const columns = Math.max(
    0,
    ...shown.map((r) => (unparsed(r) ? 1 : r.options.length)),
  );

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Grid</h1>
      <div className={styles.controls}>
        <label>
          Status
          <select value={verdict} onChange={(e) => setVerdict(e.target.value)}>
            <option value="">any</option>
            {VERDICTS.map((v) => (
              <option key={v.value} value={v.value}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Stage
          <select value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">any</option>
            {portfolio.data.vocabulary.lifecycle.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tenant
          <select value={tenant} onChange={(e) => setTenant(e.target.value)}>
            <option value="">any</option>
            {distinct(rows.map((r) => r.parties.tenant)).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Landlord
          <select value={landlord} onChange={(e) => setLandlord(e.target.value)}>
            <option value="">any</option>
            {distinct(rows.map((r) => r.parties.landlord)).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <span className={styles.spacer} />
        <span className={styles.muted}>
          {shown.length} of {rows.length} sites
        </span>
      </div>
      {!!projects.error && (
        <p className={styles.error}>
          Could not load tenants and landlords, so those filters are empty.
        </p>
      )}
      {!!dashboard.error && (
        <p className={styles.error}>
          Could not load committed and achievable capacity.
        </p>
      )}

      {shown.length === 0 ? (
        <p className={styles.muted}>
          {rows.length === 0
            ? "Grid keeps no row for any site yet."
            : "No site matches these filters."}
        </p>
      ) : (
        <div className={styles.scroller}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Site</th>
                <th>Status</th>
                <th>Status detail</th>
                {Array.from({ length: columns }, (_, i) => (
                  <th key={i}>Option {i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((tableRow) => {
                const { entry, parties, options } = tableRow;
                const row = entry.row;
                const size = sizes.byProject.get(entry.project_id);
                return (
                  <tr key={entry.project_id}>
                    <td className={styles.site}>
                      <Link to={projectSpecialistPath(entry.project_id, AGENT)}>
                        {entry.project_name}
                      </Link>
                      {(parties.tenant || parties.landlord) && (
                        <p className={styles.parties}>
                          {parties.tenant && <>T: {parties.tenant}</>}
                          {parties.tenant && parties.landlord && <br />}
                          {parties.landlord && <>L: {parties.landlord}</>}
                        </p>
                      )}
                      {size && (
                        <InstalledCapacity row={size} definitions={sizes.definitions} />
                      )}
                    </td>
                    <td>
                      {row ? (
                        <div className={styles.chips}>
                          {row.on_track && <VerdictChip onTrack={row.on_track} />}
                          <LifecycleChip lifecycle={row.lifecycle} />
                          <OwnerChip
                            who={entry.next_move.who}
                            lifecycle={row.lifecycle}
                            overdueFrom={entry.next_move.overdue_from}
                            move={entry.next_move}
                          />
                        </div>
                      ) : (
                        <span className={styles.muted}>No status yet</span>
                      )}
                    </td>
                    <td className={styles.detail}>
                      {row?.on_track?.reason || (
                        <span className={styles.muted}>-</span>
                      )}
                    </td>
                    {Array.from({ length: columns }, (_, column) => {
                      const option = options[column];
                      return (
                        <td key={column} className={styles.option}>
                          {column === 0 && unparsed(tableRow) ? (
                            <OptionsOnTheTable text={entry.row?.options ?? ""} />
                          ) : option ? (
                            <RouteCell route={option.route} match={option.match} />
                          ) : (
                            <span className={styles.muted}>-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
